/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    getFriendlyName,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import {
    buildCacheKeyWithUrlTokens,
    CurrentCategoryInput,
    getCategoryUrl,
    getCurrentCategory,
    removeDomainQspFromUrl
} from '@msdyn365-commerce-modules/retail-actions';

import { IPageSummaryData } from '@msdyn365-commerce-modules/page-summary';

import { IExtCategoryPageSummaryConfig } from './ext-category-page-summary.props.autogenerated';
import { IDefaultPageSummaryConfig } from '@msdyn365-commerce-modules/page-summary';
import { CategoryExtraDataInput, getCategoryExtraDataAction } from '../../actions/get-category-extra-data';

/**
 * Category Page Summary Input
 */
export class CategoryPageSummaryInput implements IActionInput {
    public requestContext: IRequestContext;

    public config: IExtCategoryPageSummaryConfig | IDefaultPageSummaryConfig;

    constructor(config: IExtCategoryPageSummaryConfig | IDefaultPageSummaryConfig, requestContext: IRequestContext) {
        this.config = config || {};
        this.requestContext = requestContext;
    }

    public getCacheObjectType = (): string => 'CategoryPageSummaryExt';

    public getCacheKey = (): string => buildCacheKeyWithUrlTokens('CategoryPageSummaryExt', this.requestContext);

    public dataCacheType = (): CacheType => 'request';
}

const createGetCurrentCategoryInput = (
    inputData: IActionContext,
    config?: IExtCategoryPageSummaryConfig | IDefaultPageSummaryConfig
): CurrentCategoryInput => {
    if (inputData && inputData.requestContext) {
        let includeRootCategory: boolean | undefined;
        if (config) {
            includeRootCategory = (config as IExtCategoryPageSummaryConfig).includeRootCategory;
        }
        return new CurrentCategoryInput(inputData.requestContext, includeRootCategory);
    }

    throw new Error('Please specify categoryId query string in request.');
};
/**
 * Get skip count.
 * @param inputData
 */
function getSkipCount(inputData: ICreateActionContext<IGeneric<IAny>, IGeneric<IAny>>): string | undefined {
    return inputData && inputData.requestContext && inputData.requestContext.query && inputData.requestContext.query.skip;
}

const action = async (input: CategoryPageSummaryInput, context: IActionContext): Promise<IPageSummaryData> => {
    const { config } = input;
    let currentCategoryResult;
    try {
        currentCategoryResult = await getCurrentCategory(createGetCurrentCategoryInput(context, input.config), context);
    } catch {
        // Do nothing, if the action fails fallback to values defined in data
    }

    let categoryDataExt = null;
    try {
        categoryDataExt = await getCategoryExtraDataAction(new CategoryExtraDataInput(context.requestContext, true), context);
    } catch {
        // Do nothing, if the action fails fallback to values defined in data
    }

    if (currentCategoryResult) {
        const currentCategory = { ...currentCategoryResult };
        let categoryUrl: string | undefined;
        try {
            // Reset category's name neutralizedName
            const localName = getFriendlyName(context.requestContext.locale, currentCategory.NameTranslations);
            const neutralizedName = currentCategory.NeutralizedName || currentCategory.Name;
            currentCategory.Name = localName;
            currentCategory.NeutralizedName = neutralizedName;
            categoryUrl = getCategoryUrl(currentCategory);

            const canonicalDomain = context.requestContext.canonicalDomain;
            if (categoryUrl && canonicalDomain) {
                categoryUrl = `https://${canonicalDomain}${categoryUrl}`.toLocaleLowerCase();
                const skipCount = getSkipCount(context);
                const Url = new URL(categoryUrl);
                const urlParams: URLSearchParams = new URLSearchParams(Url.search);
                if (skipCount) {
                    urlParams.set('skip', skipCount);
                }
                Url.search = urlParams.toString();
                categoryUrl = Url.toString();
                categoryUrl = removeDomainQspFromUrl(categoryUrl, context.requestContext);
            } else {
                categoryUrl = undefined;
            }
        } catch {
            categoryUrl = undefined;
        }
        return {
            title: categoryDataExt?.EcommerceMetaTitle || currentCategory.Name,
            description: categoryDataExt?.EcommerceMetaDescription,
            sharingImageUrl: currentCategory.OfflineImage,
            canonicalUrl: categoryUrl,
            faviconUrl: config && config.faviconUrl
        };

        // If the action fails fallback to values defined from data
    } else if (config) {
        return {
            title: categoryDataExt?.EcommerceMetaTitle || config.title,
            description: categoryDataExt?.EcommerceMetaDescription,
            sharingImageUrl: config.sharingImage && config.sharingImage.src,
            faviconUrl: config && config.faviconUrl
        };
    }
    return {};
};

// replace @msdyn365-commerce-modules/page-summary/category-page-summary with ext-category-page-summary to not override the original action
export default createObservableDataAction({
    id: 'ext-category-page-summary',
    action: <IAction<IPageSummaryData>>action,
    input: (args: ICreateActionContext) => {
        return new CategoryPageSummaryInput(<IDefaultPageSummaryConfig>args.config, args.requestContext);
    }
});
